import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useUIStore } from '@/stores/ui.store';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { X } from 'lucide-react';
import { POSDrawer } from '../pos/POSDrawer';
import { POSFloatingDraftDock } from '../pos/POSFloatingDraftDock';
import { StaffFormDrawer } from '../staff/StaffFormDrawer';
import { usePOSStore } from '../../stores/pos.store';
import { overlayStack } from '../../lib/overlayStack';

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const mobileDrawerOpen = useUIStore((s) => s.mobileDrawerOpen);
  const setMobileDrawerOpen = useUIStore((s) => s.setMobileDrawerOpen);
  const setSidebarCollapsed = useUIStore((s) => s.setSidebarCollapsed);

  const isStaffFormOpen = usePOSStore((s) => s.isStaffFormOpen);
  const closeStaffCreation = usePOSStore((s) => s.closeStaffCreation);
  const onStaffCreated = usePOSStore((s) => s.onStaffCreated);

  /** Register mobile drawer with overlay stack for ESC and back button */
  const overlayIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (mobileDrawerOpen) {
      overlayIdRef.current = overlayStack.push(() => setMobileDrawerOpen(false));
    } else if (overlayIdRef.current) {
      overlayStack.remove(overlayIdRef.current);
      overlayIdRef.current = null;
    }
    return () => {
      if (overlayIdRef.current) {
        overlayStack.remove(overlayIdRef.current);
        overlayIdRef.current = null;
      }
    };
  }, [mobileDrawerOpen, setMobileDrawerOpen]);

  /** Lock body scroll on mobile when drawer is open */
  useEffect(() => {
    if (window.innerWidth < 768 && mobileDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileDrawerOpen]);

  /** Handle responsive breakpoint transitions cleanly */
  useEffect(() => {
    const mdQuery = window.matchMedia('(min-width: 768px)');
    const lgQuery = window.matchMedia('(min-width: 1024px)');

    const handleBreakpointChange = () => {
      const isMd = mdQuery.matches;
      const isLg = lgQuery.matches;

      // Close mobile drawer whenever scaling up to desktop/tablet
      if (isMd) {
        setMobileDrawerOpen(false);
      }

      // Auto-collapse sidebar on tablet screens (768px - 1023px) to maximize table space
      if (isMd && !isLg) {
        setSidebarCollapsed(true);
      }
    };

    handleBreakpointChange();

    mdQuery.addEventListener('change', handleBreakpointChange);
    lgQuery.addEventListener('change', handleBreakpointChange);

    return () => {
      mdQuery.removeEventListener('change', handleBreakpointChange);
      lgQuery.removeEventListener('change', handleBreakpointChange);
    };
  }, [setMobileDrawerOpen, setSidebarCollapsed]);

  return (
    <div className="flex h-dvh w-full overflow-hidden bg-surface-secondary">
      {/* ═══════════ Inline Desktop/Tablet Sidebar (md and above) ═══════════ */}
      <aside className="hidden md:flex shrink-0 h-full">
        <Sidebar isMobile={false} />
      </aside>

      {/* ═══════════ Mobile Drawer Overlay (< 768px) ═══════════ */}
      <>
        {/* Backdrop */}
        <div
          className={`
            fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm
            transition-opacity duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
            ${mobileDrawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
          `}
          onClick={() => setMobileDrawerOpen(false)}
          aria-hidden="true"
        />

        {/* Drawer panel */}
        <div
          className={`
            fixed inset-y-0 left-0 z-[60] flex
            transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
            ${mobileDrawerOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          <Sidebar isMobile={true} />

          {/* Floating Close Button outside drawer */}
          <button
            type="button"
            onClick={() => setMobileDrawerOpen(false)}
            className={`
              ml-2 mt-3 flex h-9 w-9 items-center justify-center rounded-xl
              bg-black/50 text-white backdrop-blur-md border border-white/10
              transition-all duration-300 btn-press cursor-pointer
              ${mobileDrawerOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-90 pointer-events-none'}
            `}
            aria-label="Cerrar menú"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </>

      <div className="flex flex-1 flex-col min-w-0 h-full overflow-hidden">
        <Header />

        <main className="flex-1 overflow-y-auto px-4 pb-[calc(2.5rem+env(safe-area-inset-bottom,0px))] sm:px-6 sm:pb-[calc(3rem+env(safe-area-inset-bottom,0px))] lg:px-8 lg:pb-[calc(4rem+env(safe-area-inset-bottom,0px))]">
          <div className="mx-auto max-w-7xl animate-fade-in pt-4 sm:pt-6 lg:pt-8">
            {children}
          </div>
        </main>
      </div>

      <POSFloatingDraftDock />
      <POSDrawer />
      <StaffFormDrawer
        isOpen={isStaffFormOpen}
        onClose={closeStaffCreation}
        onSuccess={onStaffCreated}
      />
    </div>
  );
}
